import LessonMarkdown from "@/components/LessonMarkdown";
import LessonQuiz from "@/components/LessonQuiz";
import LiveClassroomPanel from "@/components/LiveClassroomPanel";
import MicroChunker from "@/components/MicroChunker";
import { Loader2, RefreshCw, Sparkles, BookOpen } from "lucide-react";
import { langToBcp47 } from "@/components/explain/constants";
import mdComponents from "@/components/explain/mdComponents";

/**
 * ExplainPartArticle — Iter84 extraction.
 *
 * Renders one of the three whitepaper parts inside ExplainDeeperSheet.
 * Either a loading skeleton (when the part is still being generated)
 * or the full <article> with LiveClassroom narration, markdown body,
 * regenerate button, MicroChunker explainer + part-quiz.
 *
 * The Part 1 article also surfaces the Mini-game + Flashcards
 * discovery CTAs at the bottom (handled here so the orchestrator
 * doesn't have to thread `setGameOpen` / `setDeckOpen` into the
 * sub-component tree — we just lift the callbacks one level up).
 */
export default function ExplainPartArticle({
  part, data, loadingPart, regenPart, moduleLang,
  pathId, stepIndex, gameRewarded,
  onRegenerate, partRefSetter, onOpenGame, onOpenDeck,
}) {
  const p = part;
  const Icon = p.Icon;

  if (!data) {
    if (loadingPart !== p.n) return null;
    return (
      <div className="space-y-3 animate-fadeup" data-testid={`part-${p.n}-loading`}>
        <div className="flex items-center gap-2 label-eyebrow">
          <Icon size={12} className={p.accent} /> Part {p.n} · {p.title}
        </div>
        <div className="h-5 rounded bg-white/5 sheen w-2/3" />
        <div className="h-3 rounded bg-white/5 sheen" />
        <div className="h-3 rounded bg-white/5 sheen w-5/6" />
        <div className="h-3 rounded bg-white/5 sheen w-4/6" />
        <div className="h-3 rounded bg-white/5 sheen w-5/6" />
        <div className="text-white/40 text-xs flex items-center gap-2 pt-1">
          <Loader2 size={12} className="animate-spin" /> Oak AI Campus is writing ~900 words…
        </div>
      </div>
    );
  }

  return (
    <article
      ref={partRefSetter}
      className="animate-fadeup scroll-mt-4"
      data-testid={`part-${p.n}-content`}
    >
      {/* Cinematic instructor + karaoke narration above each part. */}
      <LiveClassroomPanel
        markdown={data.markdown}
        partTitle={p.title}
        lang={langToBcp47(moduleLang)}
      />
      <div className="prose-oak">
        <LessonMarkdown markdown={data.markdown} components={mdComponents} />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <button
          onClick={() => onRegenerate(p.n)}
          disabled={regenPart !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border border-white/10 hover:border-white/25 text-white/55 hover:text-white/85 transition disabled:opacity-50 disabled:cursor-wait"
          data-testid={`btn-regen-part-${p.n}`}
          title="Regenerate this part if it drifted off-topic. Free for whitepapers created before 28 Apr 2026."
        >
          {regenPart === p.n ? (
            <><Loader2 size={11} className="animate-spin" /> Regenerating…</>
          ) : (
            <><RefreshCw size={11} /> Regenerate</>
          )}
        </button>
        <div className="label-eyebrow !text-[10px] text-white/40 font-mono-oak">
          {data.word_count?.toLocaleString()} words
        </div>
      </div>
      <MicroChunker
        getText={() => data.markdown || ""}
        context={`Lesson · Part ${p.n} — ${p.title}`}
        testIdPrefix={`chunker-explain-${p.n}`}
      />
      <LessonQuiz pathId={pathId} stepIndex={stepIndex} part={p.n} />
      {p.n === 1 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={onOpenGame}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-[#0044FF]/40 bg-gradient-to-r from-[#0044FF]/12 via-[#5D3FD3]/10 to-[#00FF9D]/10 hover:from-[#0044FF]/22 hover:via-[#5D3FD3]/18 hover:to-[#00FF9D]/18 text-white font-display text-sm font-semibold transition"
            data-testid="btn-open-minigame"
            title="Quick interactive game tied to this lesson"
          >
            <Sparkles size={13} className="text-[#9bc7ff]" />
            {gameRewarded ? "Replay mini-game" : "Mini-game · +1 credit"}
          </button>
          <button
            type="button"
            onClick={onOpenDeck}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-[#00FF9D]/40 bg-gradient-to-r from-[#00FF9D]/10 via-[#0044FF]/8 to-[#5D3FD3]/8 hover:from-[#00FF9D]/22 hover:via-[#0044FF]/18 hover:to-[#5D3FD3]/18 text-white font-display text-sm font-semibold transition"
            data-testid="btn-open-flashcards"
            title="Auto-generate spaced-repetition flashcards from this lesson"
          >
            <BookOpen size={13} className="text-[#9bffcc]" />
            Make flashcards · +5 XP/card
          </button>
        </div>
      )}
      {p.n !== 3 && <div className="mt-8 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />}
    </article>
  );
}
